import { z } from 'zod';

/**
 * SMS, as a DECLARATIVE HTTP request rather than a script.
 * ---------------------------------------------------------------------------
 * The operator asked for four named providers (Celcom Africa, Africa's
 * Talking, Twilio, Infobip) and for the ability to "paste in configuration —
 * headers, body and so on" so a fifth, unlisted provider still works.
 *
 * The obvious reading of that request is a scripting hook: let the operator
 * paste a snippet that builds the request. This file is the deliberate refusal
 * of that reading. A platform that evaluates operator-supplied JavaScript has
 * handed anyone who reaches the settings page — a stolen session, a confused
 * deputy, a future integration that writes settings — remote code execution on
 * the box that holds every OLT credential on this fleet. There is no sandbox
 * cheap enough to make that trade worth taking, and nothing about "send an
 * HTTP request to a gateway" needs one.
 *
 * What an SMS gateway actually varies in is small and enumerable: the method,
 * the URL, a handful of headers, whether auth is a header or HTTP Basic, the
 * shape of the body, and how the answer says yes. So that is exactly what is
 * configurable here — as DATA, with placeholder substitution for the three or
 * four values that change per message. No `eval`, no `new Function`, no
 * template engine with expressions in it. The substitution is a fixed
 * vocabulary of names (below) and nothing else; anything not in that list is
 * refused at save time rather than silently rendering as empty at 2am.
 *
 * The presets are expressed in THIS format and nothing else. That is the point
 * of them: the generic option is not a second, less-tested code path an
 * operator falls into when the listed providers fail — it is the same
 * machinery that carries Twilio, with different data in it. If a preset sends,
 * the generic option sends.
 */

// --- What may be substituted -------------------------------------------------

/**
 * The complete substitution vocabulary. `{{name}}` in a URL, header value,
 * Basic-auth field or body template is replaced with the value below;
 * everything else is literal text.
 *
 * There are no expressions, no conditionals, no function calls and no way to
 * add a name from configuration — the list is closed, and a template naming
 * something outside it is rejected when it is saved.
 */
export const SMS_PLACEHOLDERS = [
  /** Recipient in E.164 with the leading `+`, e.g. `+254712345678`. */
  'to',
  /** The same number as bare digits, e.g. `254712345678`. Several African
   *  gateways reject the `+`, and asking the operator to keep two copies of
   *  the recipient in sync is how a template ends up sending to nobody. */
  'toDigits',
  /** The message text. */
  'message',
  /** Sender id / shortcode / From number. Configuration, not a secret. */
  'senderId',
  /** Account username, where the provider has one (Africa's Talking). */
  'username',
  /** Account identifier: Twilio Account SID, Celcom partner ID. */
  'accountId',
  /** Primary credential. SECRET — encrypted at rest, never returned. */
  'apiKey',
  /** Second credential where a provider needs one (Twilio auth token).
   *  SECRET — encrypted at rest, never returned. */
  'apiSecret',
] as const;
export type SmsPlaceholder = (typeof SMS_PLACEHOLDERS)[number];

/** The two values that are credentials, and are treated as such everywhere. */
export const SMS_SECRET_PLACEHOLDERS: readonly SmsPlaceholder[] = ['apiKey', 'apiSecret'];

/** Fields an operator fills in on the settings page, per provider. */
export const SMS_CREDENTIAL_FIELDS = [
  'senderId',
  'username',
  'accountId',
  'apiKey',
  'apiSecret',
] as const;
export type SmsCredentialField = (typeof SMS_CREDENTIAL_FIELDS)[number];

/** `{{ name }}` — whitespace tolerated, because a pasted template will have it. */
export const SMS_PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

/** Every placeholder name a template mentions, in the order it mentions them. */
export function placeholdersIn(text: string): string[] {
  const found: string[] = [];
  for (const m of text.matchAll(SMS_PLACEHOLDER_PATTERN)) {
    const name = m[1];
    if (name && !found.includes(name)) found.push(name);
  }
  return found;
}

export function isSmsPlaceholder(name: string): name is SmsPlaceholder {
  return (SMS_PLACEHOLDERS as readonly string[]).includes(name);
}

// --- The request template ----------------------------------------------------

/**
 * How the body is written, and — inseparably — how a substituted value is
 * ESCAPED on its way into it.
 *
 * These two are one field on purpose. A message containing a double quote
 * pasted verbatim into a JSON body produces malformed JSON and a 400 that
 * looks like a credential problem; the same message pasted into a form body
 * without percent-encoding silently truncates at the first `&`. The format
 * decides the escaping, so the operator cannot pick a body shape and an
 * escaping rule that disagree.
 *
 *   json  the template is JSON text; values are escaped as JSON string
 *         contents, so the template writes `"text":"{{message}}"` with its
 *         own quotes and gets valid JSON out
 *   form  the template is `a={{x}}&b={{y}}`; values are percent-encoded
 *   text  the template is sent as written; values are inserted raw
 *   none  no body at all (a GET-style gateway that carries everything in the
 *         query string)
 */
export const SMS_BODY_FORMATS = ['json', 'form', 'text', 'none'] as const;
export type SmsBodyFormat = (typeof SMS_BODY_FORMATS)[number];

export const SMS_METHODS = ['GET', 'POST', 'PUT'] as const;
export type SmsMethod = (typeof SMS_METHODS)[number];

/** The Content-Type each format sends unless the template overrides it. */
export const SMS_DEFAULT_CONTENT_TYPE: Record<SmsBodyFormat, string> = {
  json: 'application/json',
  form: 'application/x-www-form-urlencoded',
  text: 'text/plain; charset=utf-8',
  none: '',
};

export interface SmsHeaderTemplate {
  name: string;
  /** May contain placeholders. The WHOLE rendered line has control characters
   *  stripped — literal text as well as substituted values, because a newline
   *  in a header value is request splitting whichever half it came from. */
  value: string;
}

/**
 * HTTP Basic as a MODE rather than as a header the operator computes.
 *
 * Twilio authenticates with `Authorization: Basic base64(sid:token)`. Leaving
 * that to the operator means asking a human to base64 a credential by hand,
 * paste the result into a header, and redo it every time the token rotates —
 * and a base64 blob in a header field is a credential this platform cannot
 * recognise, cannot redact from a log line, and cannot tell apart from
 * configuration. So Basic is a mode, its two halves are ordinary placeholder
 * templates, and the encoding happens here where the secret is known.
 */
export type SmsAuthTemplate =
  | { mode: 'none' }
  | { mode: 'basic'; username: string; password: string };

/**
 * How the gateway's ANSWER is read.
 *
 * Necessary because "the HTTP call did not throw" is not delivery and barely
 * even acceptance: Africa's Talking answers 200 with a per-recipient
 * `InvalidPhoneNumber`, Infobip answers 200 with a `REJECTED` group, and a
 * captive portal answers 200 with a login page. Every preset therefore reads
 * something out of the body, and a configuration that checks only the status
 * code is allowed but says so on the page.
 */
export interface SmsSuccessRule {
  /** Inclusive HTTP status window that is NECESSARY. Default 200–299. */
  minStatus: number;
  maxStatus: number;
  /**
   * Where in the parsed JSON body the verdict lives: dotted, with `[n]` for
   * array elements — `SMSMessageData.Recipients[0].status`. Empty means the
   * status code is the whole test.
   */
  path: string;
  /** How the value at `path` is judged. Comparisons are case-insensitive and
   *  compare the value's string form, so `200` and `"200"` are the same answer. */
  test: SmsSuccessTest;
  /** Operands for `equals` / `oneOf` / `contains`. */
  values: string[];
  /** Where the provider's own message id is, so the record can name it. */
  idPath: string;
  /** Where the provider explains a refusal, so the record can quote it. */
  errorPath: string;
}

export const SMS_SUCCESS_TESTS = ['present', 'truthy', 'equals', 'oneOf', 'contains'] as const;
export type SmsSuccessTest = (typeof SMS_SUCCESS_TESTS)[number];

export interface SmsRequestTemplate {
  method: SmsMethod;
  /** Absolute http(s) URL. May contain placeholders; they are URL-encoded. */
  url: string;
  headers: SmsHeaderTemplate[];
  auth: SmsAuthTemplate;
  bodyFormat: SmsBodyFormat;
  body: string;
  /** Overrides the format's default Content-Type. Empty means use the default. */
  contentType: string;
  success: SmsSuccessRule;
}

export const DEFAULT_SMS_SUCCESS: SmsSuccessRule = {
  minStatus: 200,
  maxStatus: 299,
  path: '',
  test: 'present',
  values: [],
  idPath: '',
  errorPath: '',
};

// --- Validation --------------------------------------------------------------

/**
 * No control character may appear in anything that becomes a header line.
 *
 * A CR or an LF is request splitting, and the rest are simply not header
 * material. The renderer strips these at send time as well — this is the gate
 * that refuses to STORE one, so an operator who pastes a value with a hard
 * line break in it is told at save time rather than sending a request nobody
 * wrote.
 */
// eslint-disable-next-line no-control-regex
const NO_CONTROL_CHARACTERS = /^[^\u0000-\u001f\u007f]*$/;

export const smsHeaderSchema = z.object({
  // RFC 7230 token. Rejecting anything else is what stops a header NAME being
  // used to inject a second header.
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/, 'Header names are letters, digits and -_.'),
  value: z
    .string()
    .max(1000)
    .regex(
      NO_CONTROL_CHARACTERS,
      'A header value cannot contain line breaks or other control characters.',
    ),
});

export const smsSuccessRuleSchema = z.object({
  minStatus: z.number().int().min(100).max(599).default(200),
  maxStatus: z.number().int().min(100).max(599).default(299),
  path: z.string().max(200).default(''),
  test: z.enum(SMS_SUCCESS_TESTS).default('present'),
  values: z.array(z.string().max(200)).max(20).default([]),
  idPath: z.string().max(200).default(''),
  errorPath: z.string().max(200).default(''),
});

export const smsRequestTemplateSchema = z.object({
  method: z.enum(SMS_METHODS),
  url: z.string().min(1).max(2000),
  headers: z.array(smsHeaderSchema).max(20).default([]),
  auth: z
    .discriminatedUnion('mode', [
      z.object({ mode: z.literal('none') }),
      z.object({
        mode: z.literal('basic'),
        username: z.string().max(300),
        password: z.string().max(300),
      }),
    ])
    .default({ mode: 'none' }),
  bodyFormat: z.enum(SMS_BODY_FORMATS),
  body: z.string().max(8000).default(''),
  /**
   * Constrained like a header VALUE, because that is exactly what it becomes.
   * It carries no placeholders, so nothing escapes it on the way out — the
   * refusal has to happen here.
   */
  contentType: z
    .string()
    .max(200)
    .regex(
      NO_CONTROL_CHARACTERS,
      'A content type cannot contain line breaks or other control characters.',
    )
    .default(''),
  success: smsSuccessRuleSchema,
});

/**
 * Everything wrong with a template, as operator-facing sentences.
 *
 * Returned rather than thrown so the settings page can show all of them at
 * once — a form that reveals one mistake per save is a form that gets saved
 * six times.
 */
export function validateSmsTemplate(t: SmsRequestTemplate): string[] {
  const problems: string[] = [];

  // Every placeholder, everywhere it can appear.
  const surfaces: Array<[string, string]> = [
    ['the URL', t.url],
    ['the body', t.body],
    ...t.headers.map((h): [string, string] => [`the “${h.name}” header`, h.value]),
  ];
  if (t.auth.mode === 'basic') {
    surfaces.push(['the Basic auth username', t.auth.username]);
    surfaces.push(['the Basic auth password', t.auth.password]);
  }
  for (const [where, text] of surfaces) {
    for (const name of placeholdersIn(text)) {
      if (!isSmsPlaceholder(name)) {
        problems.push(
          `${where} uses {{${name}}}, which is not a value this platform can fill in. Available: ${SMS_PLACEHOLDERS.map((p) => `{{${p}}}`).join(', ')}.`,
        );
      }
    }
  }

  /*
    Shape only, here. This module is shared with the browser bundle and has no
    Node types, and — more to the point — a syntactic check is all that belongs
    on this side: whether the host is one the platform may actually REACH is
    decided at send time by the SSRF guard, against DNS as it stands at that
    moment. A URL that passes here is still refused there if it resolves into
    the management network, which is where that decision has to be made.

    The placeholders are blanked first: `https://api.example.com/{{accountId}}/x`
    is a perfectly good template and not a syntactically valid URL until it is
    filled in.
   */
  const sampledUrl = t.url.replace(SMS_PLACEHOLDER_PATTERN, 'x').trim();
  const shape = /^(https?):\/\/([^\s/?#]+)([/?#][^\s]*)?$/i.exec(sampledUrl);
  if (!shape) {
    problems.push('The URL is not a URL. It must be absolute, e.g. https://api.example.com/send.');
  } else if ((shape[2] ?? '').includes('@')) {
    problems.push(
      'Credentials in the URL (user:pass@host) are not accepted — use the Basic auth fields, which keep the secret out of every log line.',
    );
  }

  if (t.method === 'GET' && t.bodyFormat !== 'none') {
    // `fetch` refuses a GET with a body outright, so this would fail at send
    // time with a message about the request object rather than about the
    // configuration — put everything in the URL, or use POST.
    problems.push(
      'A GET cannot carry a body. Set the body format to “none” and put the values in the URL, or use POST.',
    );
  }
  if (t.bodyFormat === 'none' && t.body.trim() !== '') {
    problems.push('The body format is “none”, so the body will not be sent. Choose a format.');
  }
  if (t.bodyFormat !== 'none' && t.body.trim() === '') {
    problems.push('The body is empty. Nothing would carry the message.');
  }
  if (t.bodyFormat === 'json' && t.body.trim() !== '') {
    /*
      Rendered with a sample first, because the template itself is not JSON:
      `{"text":"{{message}}"}` is only valid once the placeholder is gone. The
      sample deliberately contains a quote and a backslash — the two characters
      that turn a working template into a 400 the first time a subscriber's
      name has an apostrophe in it.
    */
    const sampled = t.body.replace(SMS_PLACEHOLDER_PATTERN, 'a\\"b');
    try {
      JSON.parse(sampled);
    } catch {
      problems.push(
        'The body is not valid JSON once the placeholders are filled in. Check the quotes, commas and braces.',
      );
    }
  }
  if (t.bodyFormat === 'form' && t.body.includes('\n')) {
    problems.push('A form body is one line of a=1&b=2 — remove the line breaks.');
  }

  const usesMessage = surfaces.some(([, text]) => placeholdersIn(text).includes('message'));
  const usesTo = surfaces.some(([, text]) => {
    const names = placeholdersIn(text);
    return names.includes('to') || names.includes('toDigits');
  });
  if (!usesMessage) problems.push('Nothing in this request carries {{message}}.');
  if (!usesTo) problems.push('Nothing in this request carries {{to}} or {{toDigits}}.');

  if (t.success.maxStatus < t.success.minStatus) {
    problems.push('The success status range ends before it starts.');
  }
  if (
    t.success.path !== '' &&
    (t.success.test === 'equals' || t.success.test === 'oneOf' || t.success.test === 'contains') &&
    t.success.values.length === 0
  ) {
    problems.push('The success rule compares against nothing — add at least one value.');
  }

  return problems;
}

/**
 * True when the configuration would call a send successful on the strength of
 * the status code alone.
 *
 * Not an error — some gateways genuinely answer with a bare 202 — but the page
 * says so, because it is the difference between "the gateway accepted this"
 * and "something answered on port 443".
 */
export function acceptsOnStatusAlone(rule: SmsSuccessRule): boolean {
  return rule.path.trim() === '';
}

// --- Providers ---------------------------------------------------------------

export const SMS_PROVIDER_IDS = [
  'africastalking',
  'twilio',
  'infobip',
  'celcom',
  'custom',
] as const;
export type SmsProviderId = (typeof SMS_PROVIDER_IDS)[number];

export interface SmsCredentialFieldSpec {
  key: SmsCredentialField;
  label: string;
  hint: string;
  required: boolean;
  secret: boolean;
}

export interface SmsProviderPreset {
  id: SmsProviderId;
  name: string;
  docsUrl: string;
  /**
   * How much this platform actually KNOWS about the contract below.
   *
   *   verified  the endpoint, auth style, body shape and success field are
   *             from the provider's published API and are expected to work as
   *             shipped
   *   check     the shape is the one this provider's resellers document, but
   *             it has not been confirmed against a live account — read the
   *             note, check it against your own dashboard before you rely on
   *             it for anything that pages somebody
   *
   * Stated rather than assumed because an invented endpoint does not fail at
   * save time. It fails at 2am, silently, on the message that was supposed to
   * say the OLT is down.
   */
  confidence: 'verified' | 'check';
  note: string;
  fields: SmsCredentialFieldSpec[];
  template: SmsRequestTemplate;
}

const FIELD: Record<SmsCredentialField, Omit<SmsCredentialFieldSpec, 'required'>> = {
  senderId: { key: 'senderId', label: 'Sender ID', hint: '', secret: false },
  username: { key: 'username', label: 'Username', hint: '', secret: false },
  accountId: { key: 'accountId', label: 'Account ID', hint: '', secret: false },
  apiKey: { key: 'apiKey', label: 'API key', hint: '', secret: true },
  apiSecret: { key: 'apiSecret', label: 'API secret', hint: '', secret: true },
};

function field(
  key: SmsCredentialField,
  over: Partial<SmsCredentialFieldSpec> & { required: boolean },
): SmsCredentialFieldSpec {
  return { ...FIELD[key], ...over };
}

/**
 * Africa's Talking — form-encoded, `apiKey` header, per-recipient verdict.
 *
 * Their v1 messaging endpoint takes `application/x-www-form-urlencoded` and
 * authenticates with a bare `apiKey` header (NOT `Authorization`), which is
 * the detail most integrations get wrong first. The answer is
 * `SMSMessageData.Recipients[]`, one entry per number, each with its own
 * `status` — `Success`, `InvalidPhoneNumber`, `InsufficientBalance` and so on.
 * A 200 with `InvalidPhoneNumber` is a 200, which is exactly why the success
 * rule reads that field rather than the status line.
 *
 * `from` is the registered sender id or shortcode and is required on
 * production accounts; sandbox accounts use the username `sandbox` against
 * `api.sandbox.africastalking.com`, which is a one-word edit to the URL.
 */
const AFRICASTALKING: SmsProviderPreset = {
  id: 'africastalking',
  name: "Africa's Talking",
  docsUrl: 'https://developers.africastalking.com/docs/sms/sending/bulk',
  confidence: 'verified',
  note:
    'Sandbox accounts use username “sandbox” and the host api.sandbox.africastalking.com — change the URL to test without spending credit. The sender ID must be a shortcode or alphanumeric ID already registered on the account.',
  fields: [
    field('username', {
      required: true,
      hint: 'The account username, or “sandbox” for the test environment.',
    }),
    field('apiKey', { required: true, hint: 'From Settings → API Key in the AT dashboard.' }),
    field('senderId', {
      required: true,
      label: 'Sender ID / shortcode',
      hint: 'The registered alphanumeric sender or shortcode messages come from.',
    }),
  ],
  template: {
    method: 'POST',
    url: 'https://api.africastalking.com/version1/messaging',
    headers: [
      { name: 'apiKey', value: '{{apiKey}}' },
      { name: 'Accept', value: 'application/json' },
    ],
    auth: { mode: 'none' },
    bodyFormat: 'form',
    body: 'username={{username}}&to={{to}}&message={{message}}&from={{senderId}}',
    contentType: '',
    success: {
      minStatus: 200,
      maxStatus: 299,
      path: 'SMSMessageData.Recipients[0].status',
      test: 'equals',
      values: ['Success'],
      idPath: 'SMSMessageData.Recipients[0].messageId',
      errorPath: 'SMSMessageData.Recipients[0].status',
    },
  },
};

/**
 * Twilio — HTTP Basic, form-encoded, `sid` in the answer.
 *
 * `POST /2010-04-01/Accounts/{AccountSid}/Messages.json`, authenticated with
 * Basic where the username is the Account SID and the password is the auth
 * token. Success is 201 with a `sid`; a refusal is a 4xx carrying `code` and
 * `message` (`21211 The 'To' number … is not a valid phone number`). The
 * success rule therefore asks for the `sid` rather than for `status`, because
 * `status` on a fresh message is `queued` — accepted, not delivered, and this
 * platform should not claim more than the gateway did.
 *
 * To send from a Messaging Service instead of a number, replace `From` in the
 * body with `MessagingServiceSid`.
 */
const TWILIO: SmsProviderPreset = {
  id: 'twilio',
  name: 'Twilio',
  docsUrl: 'https://www.twilio.com/docs/messaging/api/message-resource',
  confidence: 'verified',
  note:
    'The From must be a Twilio number or sender you own, in E.164. To send from a Messaging Service, change “From” to “MessagingServiceSid” in the body and put the MG… id in Sender ID.',
  fields: [
    field('accountId', {
      required: true,
      label: 'Account SID',
      hint: 'AC… — it goes in the URL and is the Basic auth username.',
    }),
    field('apiSecret', {
      required: true,
      label: 'Auth token',
      hint: 'The Basic auth password. Kept encrypted; never shown again.',
    }),
    field('senderId', {
      required: true,
      label: 'From number',
      hint: 'E.164, e.g. +15005550006.',
    }),
  ],
  template: {
    method: 'POST',
    url: 'https://api.twilio.com/2010-04-01/Accounts/{{accountId}}/Messages.json',
    headers: [{ name: 'Accept', value: 'application/json' }],
    auth: { mode: 'basic', username: '{{accountId}}', password: '{{apiSecret}}' },
    bodyFormat: 'form',
    body: 'To={{to}}&From={{senderId}}&Body={{message}}',
    contentType: '',
    success: {
      minStatus: 200,
      maxStatus: 299,
      path: 'sid',
      test: 'present',
      values: [],
      idPath: 'sid',
      errorPath: 'message',
    },
  },
};

/**
 * Infobip — `Authorization: App <key>`, JSON, status group in the answer.
 *
 * `POST /sms/2/text/advanced` with `{"messages":[{destinations, from, text}]}`.
 * The answer carries a per-message `status` object whose `groupName` is the
 * verdict: `PENDING` (accepted for delivery) or `DELIVERED` are good,
 * `REJECTED` / `UNDELIVERABLE` / `EXPIRED` are not — and all of them come back
 * with HTTP 200.
 *
 * THE HOST IS PER-ACCOUNT. Infobip issues each customer a personalised base
 * URL (`xxxxx.api.infobip.com`) and the generic host will not authenticate.
 * That is one field to correct and the note says so, which is better than a
 * preset that looks configured and answers 401.
 */
const INFOBIP: SmsProviderPreset = {
  id: 'infobip',
  name: 'Infobip',
  docsUrl: 'https://www.infobip.com/docs/api/channels/sms',
  confidence: 'verified',
  note:
    'Replace api.infobip.com in the URL with YOUR account’s base URL — Infobip gives each account a personalised host such as xxxxx.api.infobip.com, shown on the API key page. The generic host will answer 401.',
  fields: [
    field('apiKey', {
      required: true,
      hint: 'Sent as “Authorization: App <key>”. From the Infobip API keys page.',
    }),
    field('senderId', {
      required: true,
      label: 'Sender',
      hint: 'Alphanumeric sender or number registered on the account.',
    }),
  ],
  template: {
    method: 'POST',
    url: 'https://api.infobip.com/sms/2/text/advanced',
    headers: [
      { name: 'Authorization', value: 'App {{apiKey}}' },
      { name: 'Accept', value: 'application/json' },
    ],
    auth: { mode: 'none' },
    bodyFormat: 'json',
    body: '{"messages":[{"destinations":[{"to":"{{toDigits}}"}],"from":"{{senderId}}","text":"{{message}}"}]}',
    contentType: '',
    success: {
      minStatus: 200,
      maxStatus: 299,
      path: 'messages[0].status.groupName',
      test: 'oneOf',
      values: ['PENDING', 'DELIVERED'],
      idPath: 'messages[0].messageId',
      errorPath: 'messages[0].status.description',
    },
  },
};

/**
 * Celcom Africa — the Kenyan bulk-SMS shape, and the one to CHECK.
 *
 * Celcom's bulk API is the `isms`/Advanta-style JSON endpoint used by several
 * Kenyan resellers: a JSON body carrying `apikey`, `partnerID`, `shortcode`,
 * `mobile` and `message`, answering with `responses[]` whose `response-code`
 * is 200 for accepted and 1001–1011 for the various refusals (invalid sender
 * id, invalid credentials, insufficient balance).
 *
 * Marked `check` rather than `verified` deliberately: that shape is what this
 * family of gateways documents, but it has not been confirmed against a live
 * Celcom account here, and Celcom hands each customer their own API host. The
 * operator should confirm the URL and the partner ID against their own
 * dashboard before this carries anything that matters. Everything else in this
 * file will still work if they have to change all four fields — that is what
 * makes it a template rather than a hard-coded provider.
 */
const CELCOM: SmsProviderPreset = {
  id: 'celcom',
  name: 'Celcom Africa',
  docsUrl: 'https://celcomafrica.com/',
  confidence: 'check',
  note:
    'UNCONFIRMED against a live account. Celcom issues a per-customer API host, so check the URL, and the field names in the body, against the API document on your own dashboard before relying on this. Send a test message to yourself first — the response below will show exactly what came back.',
  fields: [
    field('apiKey', { required: true, hint: 'The API key from the Celcom dashboard.' }),
    field('accountId', {
      required: true,
      label: 'Partner ID',
      hint: 'The numeric partnerID issued with the API key.',
    }),
    field('senderId', {
      required: true,
      label: 'Shortcode / sender ID',
      hint: 'The registered shortcode messages are sent from.',
    }),
  ],
  template: {
    method: 'POST',
    url: 'https://isms.celcomafrica.com/api/services/sendsms/',
    headers: [{ name: 'Accept', value: 'application/json' }],
    auth: { mode: 'none' },
    bodyFormat: 'json',
    body: '{"apikey":"{{apiKey}}","partnerID":"{{accountId}}","shortcode":"{{senderId}}","mobile":"{{toDigits}}","message":"{{message}}"}',
    contentType: '',
    success: {
      minStatus: 200,
      maxStatus: 299,
      path: 'responses[0].response-code',
      test: 'oneOf',
      values: ['200', '1000'],
      idPath: 'responses[0].messageid',
      errorPath: 'responses[0].response-description',
    },
  },
};

/**
 * The generic option: the same template, empty.
 *
 * Not a fallback code path — literally the same fields the four presets above
 * are written in, which is the whole reason it can be trusted. An operator who
 * pastes their provider's curl example into these boxes is configuring the
 * machinery that already carries Twilio.
 */
const CUSTOM: SmsProviderPreset = {
  id: 'custom',
  name: 'Another provider',
  docsUrl: '',
  confidence: 'check',
  note:
    'Copy the request out of your provider’s API document. Put credentials in the fields below and reference them as {{apiKey}} / {{apiSecret}} rather than pasting them into a header — that is what keeps them encrypted at rest and out of the log.',
  fields: [
    field('senderId', { required: false, hint: 'If your provider needs one.' }),
    field('username', { required: false, hint: 'If your provider needs one.' }),
    field('accountId', { required: false, hint: 'If your provider needs one.' }),
    field('apiKey', { required: false, hint: 'Referenced as {{apiKey}}.' }),
    field('apiSecret', { required: false, hint: 'Referenced as {{apiSecret}}.' }),
  ],
  template: {
    method: 'POST',
    url: 'https://',
    headers: [
      { name: 'Authorization', value: 'Bearer {{apiKey}}' },
      { name: 'Accept', value: 'application/json' },
    ],
    auth: { mode: 'none' },
    bodyFormat: 'json',
    body: '{"to":"{{to}}","from":"{{senderId}}","text":"{{message}}"}',
    contentType: '',
    success: { ...DEFAULT_SMS_SUCCESS },
  },
};

export const SMS_PRESETS: readonly SmsProviderPreset[] = [
  AFRICASTALKING,
  TWILIO,
  INFOBIP,
  CELCOM,
  CUSTOM,
];

export function smsPreset(id: SmsProviderId): SmsProviderPreset {
  return SMS_PRESETS.find((p) => p.id === id) ?? CUSTOM;
}

/** A deep copy, so applying a preset cannot alias the module-level constant. */
export function smsPresetTemplate(id: SmsProviderId): SmsRequestTemplate {
  return JSON.parse(JSON.stringify(smsPreset(id).template)) as SmsRequestTemplate;
}

// --- Recipients --------------------------------------------------------------

/**
 * E.164, or a reason it is not.
 *
 * Normalised once, here, rather than per template: `{{to}}` and `{{toDigits}}`
 * have to be the same number in two spellings, and the only way to guarantee
 * that is for both to come from one parse.
 */
export function normaliseMsisdn(raw: string): { ok: true; e164: string; digits: string } | { ok: false; problem: string } {
  const stripped = raw.replace(/[\s()\-.]/g, '');
  if (stripped === '') return { ok: false, problem: 'Enter a phone number.' };
  const digits = stripped.startsWith('+') ? stripped.slice(1) : stripped;
  if (!/^[1-9]\d{6,14}$/.test(digits)) {
    return {
      ok: false,
      problem:
        'That is not an international number. Use the country code and no leading zero, e.g. +254712345678.',
    };
  }
  return { ok: true, e164: `+${digits}`, digits };
}

// --- What a send produced ----------------------------------------------------

/**
 * `verification` covers both a number being proved and a sign-in code. One
 * kind rather than two because the record must not distinguish them: the
 * stored copy of either is replaced with a fixed sentence (see `record` in
 * `services/sms/send.ts`), so a six-month-old send record cannot become a
 * second place to find somebody's authentication code.
 */
export const SMS_MESSAGE_KINDS = ['test', 'notification', 'billing_link', 'verification'] as const;
export type SmsMessageKind = (typeof SMS_MESSAGE_KINDS)[number];

export interface SmsSendOutcome {
  ok: boolean;
  /** Operator-facing, already stripped of every credential. */
  detail: string;
  httpStatus: number | null;
  providerMessageId: string | null;
  latencyMs: number;
  /** First part of the response body, redacted and capped. */
  responseSnippet: string;
}

/** One row of the record, as it leaves the API. Never carries a credential. */
export interface SmsMessageRecord {
  id: string;
  at: number;
  kind: SmsMessageKind;
  provider: string;
  to: string;
  message: string;
  ok: boolean;
  httpStatus: number | null;
  providerMessageId: string | null;
  detail: string;
  responseSnippet: string;
  /** Method and URL, query string dropped and secrets redacted. */
  request: string;
  /** Header NAMES only — a value can be the credential itself. */
  headerNames: string[];
  latencyMs: number;
  by: string | null;
}

/** The settings page's view. There is no field on it that is a credential. */
export interface SmsSettingsView {
  enabled: boolean;
  provider: SmsProviderId;
  template: SmsRequestTemplate;
  senderId: string;
  username: string;
  accountId: string;
  /**
   * Whether each secret is stored, and its LAST FOUR characters — enough to
   * tell two keys apart in a field that cannot show its value, and never more
   * than that.
   */
  apiKeySet: boolean;
  apiKeyHint: string;
  apiSecretSet: boolean;
  apiSecretHint: string;
  timeoutMs: number;
  configured: boolean;
  problems: string[];
  statusOnly: boolean;
  presets: readonly SmsProviderPreset[];
  placeholders: readonly SmsPlaceholder[];
}

export const smsSettingsPatchSchema = z.object({
  enabled: z.boolean().optional(),
  provider: z.enum(SMS_PROVIDER_IDS).optional(),
  /** Replace the whole template. Sent by the form on every save. */
  template: smsRequestTemplateSchema.optional(),
  /** Take the named preset's template as it ships, discarding local edits. */
  applyPreset: z.enum(SMS_PROVIDER_IDS).optional(),
  senderId: z.string().max(64).optional(),
  username: z.string().max(200).optional(),
  accountId: z.string().max(200).optional(),
  /** Empty string means "leave it alone", so a blank field cannot silently
   *  delete a credential on save. `clearApiKey` is how one is removed. */
  apiKey: z.string().max(500).optional(),
  apiSecret: z.string().max(500).optional(),
  clearApiKey: z.boolean().optional(),
  clearApiSecret: z.boolean().optional(),
  timeoutMs: z.number().int().min(2000).max(60_000).optional(),
});
export type SmsSettingsPatch = z.infer<typeof smsSettingsPatchSchema>;

export const sendTestSmsSchema = z.object({
  to: z.string().min(3).max(32),
  message: z.string().min(1).max(480).optional(),
});

/** What a test says when the operator does not write their own. */
export const SMS_TEST_MESSAGE = 'SimpleACS test message. If you can read this, the gateway works.';
